// src/components/DApp/TxStatusCard.tsx
import React, { useEffect, useState } from "react";

export interface TxStatusCardProps {
  txStatus: "pending" | "confirmed" | "failed" | null;
  txHash: string | null;
  userAddress: string | null;
}

const TxStatusCard: React.FC<TxStatusCardProps> = ({
  txStatus: initialStatus,
  txHash: initialHash,
  userAddress,
}) => {
  // Local state for status and hash
  const [txStatus, setTxStatus] = useState(initialStatus);
  const [txHash, setTxHash] = useState(initialHash);
  const [copied, setCopied] = useState(false);

  // Clear status and hash after 15 seconds when finalized
  useEffect(() => {
    if ((txStatus === "confirmed" || txStatus === "failed") && txHash) {
      const timer = setTimeout(() => {
        setTxStatus(null);
        setTxHash(null);
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [txStatus, txHash]);

  // Copy transaction hash to clipboard
  const copyHash = () => {
    if (userAddress && txHash) {
      navigator.clipboard.writeText(txHash);
      setCopied(true);
      setTimeout(() => setCopied(false), 15000);
    }
  };

  // Don't render if no status or hash present
  if (!txStatus && !txHash) return null;

  // Determine badge color based on status
  const getColorClass = () => {
    switch (txStatus) {
      case "pending":
        return "bg-blue-500 text-white";
      case "confirmed":
        return "bg-green-500 text-white";
      case "failed":
        return "bg-red-500 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  // Explorer link for transaction
  const explorerLink = txHash ? `https://basescan.org/tx/${txHash}` : undefined;

  return (
    <div className="space-y-3 text-xs text-white">
      {/* Status badge */}
      {txStatus && (
        <div
          className={`p-2 backdrop-blur uppercase font-bold text-[10px] text-center ${getColorClass()}`}
        >
          {txStatus}
        </div>
      )}

      {/* Transaction hash and copy control */}
      {txHash && (
        <div className="bg-white/10 p-2 text-[10px] break-words flex flex-col gap-1">
          <div className="flex flex-col text-xs bg-black/20 px-3 py-2 text-center border border-white/20 rounded">
            <span className="text-[var(--color-primary-light)] font-bold uppercase tracking-wide mb-1">
              Transaction Hash
            </span>

            <a
              href={explorerLink}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono break-words text-green-700/80 hover:text-white transition"
              title="View on BaseScan"
            >
              {txHash}
            </a>
          </div>

          {/* Copy button or confirmation */}
          {copied ? (
            <button
              disabled
              className="bg-green-600 text-[10px] px-2 py-1 rounded opacity-90 cursor-not-allowed transition duration-500 ease-out transform scale-110 animate-fade-in"
            >
              Copied!
            </button>
          ) : (
            <button
              onClick={copyHash}
              className="bg-white/20 hover:bg-white/30 text-[10px] px-2 py-1 rounded transition duration-300"
            >
              Copy Hash
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TxStatusCard;
